import React, { useCallback, useContext, useEffect, useState } from "react";

import { useSnackbar } from "notistack";
import { useHistory, useLocation } from "react-router-dom";
import { Paper, Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Header from "../../../../components/Header";
import Loading from "../../../../components/Loading";

import Button from "../../../../componentsv2/Button";
import GridContainer from "../../../../componentsv2/GridContainer";
import GridItem from "../../../../componentsv2/GridItem";
import Container from "../../../../componentsv2/Container";
import { Input } from "../../../../componentsv2/Input";

import formatarData from "../../../../utils/formatarData";
import converteData from "../../../../utils/converteData";
import { OPCOES_MEDICACAO_ORIGEM } from "../../constants/agendamentoOptions";
import { abrirNoMaps, temEnderecoMinimo } from "../../utils/abrirNoMaps";
import { formatarMetadadosEvolucao } from "../../utils/evolucaoFormatters";

import { Context } from "../../../../Context/PermissoesContext";

import { api } from "../../../../services/Api";

export default function VisualizarAgendamento() {
  const { enqueueSnackbar } = useSnackbar();
  const [procedimentoSelecionado, setProcedimentoSelecionado] = useState([]);
  const { checarPermissao } = useContext(Context);
  const [situacao, setSituacao] = useState("");
  const [loading, setLoading] = useState(false);
  const [cliente, setCliente] = useState({
    id: "",
    nome: "",
    rg: "",
    cpf: "",
    mae: "",
    data_nascimento: "",
    endereco: "",
    endereco_numero: "",
    bairro: "",
    cidade: "",
    celular: "",
  });
  const [endereco, setEndereco] = useState("");
  const [cep, setCep] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [complemento, setComplemento] = useState("");
  const [observacao, setObservacao] = useState("");
  const [data, setData] = useState("");
  const [hora, setHora] = useState("");
  const [numeroEndereco, setNumeroEndereco] = useState("");
  const [estado, setEstado] = useState("");
  const [totalComDesconto, setTotalComDesconto] = useState(0);
  const [total, setTotal] = useState(0);
  const [formaPagamento, setFormaPagamento] = useState("");
  const [localAtendimento, setLocalAtendimento] = useState("");
  const [medicacaoOrigem, setMedicacaoOrigem] = useState("");
  const [pagamentoStatus, setPagamentoStatus] = useState("");
  const [prescricoes, setPrescricoes] = useState([]);
  const [loadingPrescricoes, setLoadingPrescricoes] = useState(false);
  const [evolucoes, setEvolucoes] = useState([]);
  const [loadingEvolucoes, setLoadingEvolucoes] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const carregarPrescricoes = useCallback(
    async (agendamentoId) => {
      if (!agendamentoId) return;

      try {
        setLoadingPrescricoes(true);
        const {
          data: { data },
        } = await api.get(`/v2/agendaCare/agendamento/${agendamentoId}/prescricoes`);
        setPrescricoes(Array.isArray(data) ? data : []);
      } catch (error) {
        enqueueSnackbar(error.response.data.messages, {
          variant: "warning",
        });
      } finally {
        setLoadingPrescricoes(false);
      }
    },
    [enqueueSnackbar]
  );

  const handleDownloadPrescricao = async (prescricao) => {
    if (!location.state?.row?.id || !prescricao?.id) return;

    try {
      const resposta = await api.get(
        `/v2/agendaCare/agendamento/${location.state.row.id}/prescricoes/${prescricao.id}/download`,
        { responseType: "blob" }
      );

      const blob = new Blob([resposta.data], {
        type: prescricao.mimeType || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = prescricao.nomeOriginal || "prescricao";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar(error.response.data.messages, {
        variant: "error",
      });
    }
  };

  const carregarEvolucoes = useCallback(
    async (agendamentoId) => {
      if (!agendamentoId) return;

      try {
        setLoadingEvolucoes(true);
        const {
          data: { data },
        } = await api.get(`/v2/agendaCare/agendamento/${agendamentoId}/evolucoes`);
        setEvolucoes(Array.isArray(data) ? data : []);
      } catch (error) {
        enqueueSnackbar(error.response.data.messages, {
          variant: "warning",
        });
      } finally {
        setLoadingEvolucoes(false);
      }
    },
    [enqueueSnackbar]
  );

  const calculaDesconto = (procedimentos, desconto) => {
    let somatoria = 0;
    procedimentos.forEach((el) => {
      somatoria += parseFloat(el.precoExame);
    });
    setTotal(somatoria.toFixed(2));
    setTotalComDesconto((somatoria - parseFloat(desconto)).toFixed(2));
  };

  const pegarDadosAgendamento = useCallback(async () => {
    setLoading(true);
    if (location.state?.row) {
      try {
        const {
          data: { data },
        } = await api.get(`v2/agendaCare/agendamento/${location.state.row.id}`);

        setProcedimentoSelecionado(data.procedimentos);
        setData(formatarData(new Date(data.data), false));
        setHora(data.hora);
        setSituacao(data.situacao);
        setCliente(data.cliente);
        setEndereco(data.endereco);
        setCep(data.cep);
        setBairro(data.bairro);
        setCidade(data.cidade);
        setComplemento(data.complemento);
        setObservacao(data.observacoes);
        setNumeroEndereco(data.enderecoNumero);
        setFormaPagamento(data.formaPagamento);
        setLocalAtendimento(data.localAtendimento);
        setMedicacaoOrigem(data.medicacaoOrigem);
        setPagamentoStatus(data.pagamentoStatus);
        setEstado(data.estado);
        calculaDesconto(data.procedimentos, data.desconto);
        await carregarPrescricoes(location.state.row.id);
        await carregarEvolucoes(location.state.row.id);
      } catch (error) {
        enqueueSnackbar(error.response.data.messages, {
          variant: "error",
        });
      }
    } else {
      history.push("/agendaCare");
    }
    setLoading(false);
  }, [carregarEvolucoes, carregarPrescricoes, enqueueSnackbar, history, location.state?.row]);

  useEffect(() => {
    pegarDadosAgendamento();
  }, [pegarDadosAgendamento]);

  return (
    <>
    <Header titulo="Visualizar Agendamentos"/>
    <Container>
      <Button onClick={() => history.push({ pathname: "/agendaCare" })}>
        <ArrowBackIcon style={{ color: "#003399" }} />
      </Button>
      <Paper style={{ padding: "40px", marginTop: "10px" }} elevation={2}>
        <form>
          <GridContainer spacing={2} xs={12}>
            <GridContainer item sm={6} xs={12} spacing={2}>
              <GridItem xs={4}>
                <Input
                  inputProps={{ readOnly: true }}
                  label="Data"
                  type="text"
                  variant="standard"
                  value={data}
                  required
                />
              </GridItem>
              <GridItem xs={4}>
                <Input
                  inputProps={{ readOnly: true }}
                  label="Hora"
                  type="text"
                  variant="standard"
                  value={hora}
                  required
                />
              </GridItem>
              <GridItem xs={4}>
                <Input
                  inputProps={{ readOnly: true }}
                  label="Situação"
                  type="text"
                  variant="standard"
                  value={situacao}
                  required
                />
              </GridItem>

              <GridItem xs={12}>
                <Input
                  inputProps={{ readOnly: true }}
                  multiline
                  maxRows={5}
                  required
                  fullWidth
                  value={cliente.nome}
                  label="Nome"
                  type="text"
                  variant="standard"
                />
              </GridItem>

              <GridItem xs={8}>
                <Input
                  inputProps={{ readOnly: true }}
                  required
                  fullWidth
                  value={endereco}
                  label="Endereço"
                  type="text"
                  variant="standard"
                />
              </GridItem>

              <GridItem xs={4}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={numeroEndereco}
                  required
                  fullWidth
                  label="Numero"
                  type="text"
                  variant="standard"
                />
              </GridItem>
              <GridItem xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  size="small"
                  variant="text"
                  disabled={
                    !temEnderecoMinimo({
                      endereco,
                      cidade,
                      cep,
                    })
                  }
                  onClick={() =>
                    abrirNoMaps({
                      endereco,
                      numero: numeroEndereco,
                      bairro,
                      cidade,
                      estado,
                      cep,
                    })
                  }
                >
                  Abrir no Maps
                </Button>
              </GridItem>

              <GridItem xs={10}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={cep}
                  required
                  fullWidth
                  label="CEP"
                  type="text"
                  variant="standard"
                />
              </GridItem>
              <GridItem xs={2}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={estado}
                  required
                  fullWidth
                  label="Estado"
                  type="text"
                  variant="standard"
                ></Input>
              </GridItem>
              <GridItem xs={6}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={bairro}
                  required
                  fullWidth
                  label="Bairro"
                  type="text"
                  variant="standard"
                ></Input>
              </GridItem>
              <GridItem xs={6}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={cidade}
                  required
                  fullWidth
                  label="Cidade"
                  type="text"
                  variant="standard"
                ></Input>
              </GridItem>
              <GridItem xs={12}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={complemento}
                  fullWidth
                  label="Complemento"
                  type="text"
                  variant="standard"
                ></Input>
              </GridItem>

              <GridItem xs={12}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={observacao}
                  multiline
                  maxRows={5}
                  fullWidth
                  label="Observações"
                  type="text"
                  variant="standard"
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={formaPagamento}
                  label="Forma de pagamento"
                  type="text"
                  variant="standard"
                  fullWidth
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={localAtendimento}
                  label="Local"
                  type="text"
                  variant="standard"
                  fullWidth
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={
                    OPCOES_MEDICACAO_ORIGEM.find(
                      (opcao) => opcao.value === medicacaoOrigem
                    )?.label || medicacaoOrigem
                  }
                  label="Medicação"
                  type="text"
                  variant="standard"
                  fullWidth
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <Input
                  inputProps={{ readOnly: true }}
                  value={pagamentoStatus}
                  label="Pagamento"
                  type="text"
                  variant="standard"
                  fullWidth
                />
              </GridItem>
              {checarPermissao("AGC002") && (
                <>
                  <GridItem xs={12} sm={4}>
                  <Input
                    inputProps={{ readOnly: true }}
                    value={total}
                    label="Total"
                    type="number"
                    variant="standard"
                    fullWidth
                  />
                </GridItem>
                  <GridItem xs={12} sm={4}>
                  <Input
                    inputProps={{ readOnly: true }}
                    value={totalComDesconto}
                    fullWidth
                    label="Total com desconto"
                    type="number"
                    variant="standard"
                  />
                  </GridItem>
                </>
              )}
              <GridItem xs={12}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography color="#000000">Informaçoes sobre o cliente</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {cliente.rg && (
                      <Typography color="#000000">
                        <strong>RG:</strong> {cliente.rg}{" "}
                      </Typography>
                    )}
                    {cliente.cpf && (
                      <Typography color="#000000">
                        <strong>CPF: </strong> {cliente.cpf}
                      </Typography>
                    )}
                    {cliente.mae && (
                      <Typography color="#000000">
                        <strong>Mãe: </strong> {cliente.mae}
                      </Typography>
                    )}
                    {cliente.data_nascimento && (
                      <Typography color="#000000">
                        <strong> Data Nascimento: </strong> {converteData(cliente.data_nascimento.split("T")[0])}
                      </Typography>
                    )}
                    {cliente.endereco && (
                      <Typography color="#000000">
                        <strong>Endereço: </strong>
                        {cliente.endereco}
                      </Typography>
                    )}
                    {cliente.endereco_numero && (
                      <Typography color="#000000">
                        <strong>Nº Endereço: </strong>
                        {cliente.endereco_numero}
                      </Typography>
                    )}
                    {cliente.bairro && (
                      <Typography color="#000000">
                        <strong>Bairro:</strong> {cliente.bairro}
                      </Typography>
                    )}
                    {cliente.cidade && (
                      <Typography color="#000000">
                        {" "}
                        <strong>Cidade:</strong> {cliente.cidade}
                      </Typography>
                    )}
                     {cliente.estado && (
                      <Typography color="#000000">
                        {" "}
                        <strong>Estado:</strong> {cliente.estado}
                      </Typography>
                    )}
                    {cliente.celular && (
                      <Typography color="#000000">
                        <strong>Celular:</strong> {cliente.celular}
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </GridItem>
            </GridContainer>

            <GridItem sm={6} xs={12}>
              {procedimentoSelecionado &&
                procedimentoSelecionado.map(procedimento => (
                  <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ width: "85%", flexShrink: 0 }} color="#000000">
                        {procedimento.nome}
                      </Typography>
                      {checarPermissao("AGC002") && (
                        <Typography color="#000000">R${procedimento.precoExame}</Typography>
                      )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="#000000">
                        <strong>Tempo estimado: </strong>
                        {procedimento.tempoEstimado}
                      </Typography>
                      <Typography color="#000000">
                        <strong>Descrição: </strong>
                        {procedimento.descricao}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              <div style={{ marginTop: "20px" }}>
                <Typography color="#000000" sx={{ marginBottom: "8px" }}>
                  <strong>Prescrições</strong>
                </Typography>
                {prescricoes.map((prescricao) => (
                  <div
                    key={prescricao.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                      gap: "8px",
                    }}
                  >
                    <Typography color="#000000">{prescricao.nomeOriginal}</Typography>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => handleDownloadPrescricao(prescricao)}
                    >
                      Baixar
                    </Button>
                  </div>
                ))}
                {!loadingPrescricoes && prescricoes.length === 0 && (
                  <Typography color="#666666">Nenhuma prescrição anexada.</Typography>
                )}
              </div>
              <div style={{ marginTop: "20px" }}>
                <Typography color="#000000" sx={{ marginBottom: "8px" }}>
                  <strong>Evolução do paciente</strong>
                </Typography>
                <div
                  style={{
                    marginTop: "10px",
                    height: "165px",
                    overflowY: "auto",
                    paddingRight: "6px",
                  }}
                >
                  {evolucoes.map((evolucao) => {
                    const metadados = formatarMetadadosEvolucao(evolucao);
                    return (
                      <div
                        key={evolucao.id}
                        style={{
                          padding: "6px 0",
                          marginBottom: "4px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "4px",
                            gap: "8px",
                          }}
                        >
                          <div style={{ fontSize: "12px", color: "#616161", lineHeight: 1.3 }}>
                            <strong>{metadados.usuario}</strong> - {metadados.dataHora}
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            marginBottom: "4px",
                            lineHeight: 1.35,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {evolucao.descricao}
                        </div>
                      </div>
                    );
                  })}
                  {!loadingEvolucoes && evolucoes.length === 0 && (
                    <Typography color="#666666">Nenhuma evolução registrada.</Typography>
                  )}
                </div>
              </div>
            </GridItem>
          </GridContainer>
        </form>
      </Paper>
      </Container>
    { loading && <Loading /> }
    </>
  );
}
